import {
  computed,
  DestroyRef,
  Directive,
  inject,
  input,
  Signal,
} from '@angular/core';
import { Role } from '@shared/enums';
import { tap, catchError, finalize, EMPTY } from 'rxjs';
import { LOGIN_MODAL_SOURCES, LOGIN_TOASTS } from '../constants/login.constant';
import { LoginFormService } from '../login-form.service';
import { waitForNavigationEnd } from '../operators/wait-for-login-navigation.operator';
import { ToastService } from '@shared/components/toast';
import { AuthService } from '@app/core/services/auth/auth.service';
import { Router } from '@angular/router';
import { DialogRef } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TenantInitialNavigationService } from '@shared/services/tenant-initial-navigation.service';
import { TenantFacadeService } from '@shared/features/tenant/shared/services/tenant-facade.service';
import { LoginModalConfig } from '../interfaces/login-modal-config.interface';
import { AuthNavigationService } from '@domains/auth/auth-navigation.service';

@Directive({
  selector: '[appLoginSubmit]',
  host: {
    '(ngSubmit)': 'onSubmit()',
  },
})
export class LoginSubmitDirective {
  public readonly _config = input.required<LoginModalConfig>({
    alias: 'appLoginSubmit',
  });

  private readonly _loginFormService = inject(LoginFormService);
  private readonly _toastService = inject(ToastService);
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _authNavigationService = inject(AuthNavigationService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _tenantInitialNavigationService = inject(
    TenantInitialNavigationService,
  );
  private readonly _tenantFacade = inject(TenantFacadeService);

  private readonly _redirectionPathsForCreatingApp: Signal<string> = computed(
    () => {
      const tenantPaths = this._tenantFacade.tenantEventNavigationPaths();

      return tenantPaths?.subscriber?.fromRegister ?? '';
    },
  );

  private readonly _setIsLoading = (loading: boolean) =>
    this._loginFormService.setIsLoading(loading);
  private readonly _loginForm = this._loginFormService.form;

  protected onSubmit() {
    const loginParams = {
      username: this._loginForm.controls.username.value.trim().toLowerCase(),
      password: this._loginForm.controls.password.value.trim(),
    };

    this._setIsLoading(true);
    this._dialogRef.disableClose = true;
    this._loginForm.disable();

    this._authService
      .login(loginParams)
      .pipe(
        tap((isAuthenticated) => {
          if (!isAuthenticated) throw new Error('Authentication failed');
        }),

        tap(() => this._redirectBasedOnRole()),
        waitForNavigationEnd(this._router, this._toastService),
        catchError(() => this._handleLoginError()),
        finalize(() => {
          this._setIsLoading(false);
          this._loginForm.enable();
          this._dialogRef.disableClose = false;
          this._dialogRef.close();
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  private _redirectBasedOnRole() {
    const { role, source } = this._config();

    if (role === Role.Clerk)
      return this._authNavigationService.navigateToAdminDashboard();

    if (source === LOGIN_MODAL_SOURCES.account_view)
      return this._tenantInitialNavigationService.unAwareNavigateTo(
        this._redirectionPathsForCreatingApp(),
      );

    return this._authNavigationService.goToApplications();
  }

  private _handleLoginError() {
    if (this._config().role === Role.Clerk) {
      this._toastService.show(LOGIN_TOASTS['notAuthorizedClerk']);
      return EMPTY;
    }
    this._toastService.show(LOGIN_TOASTS['incorrectCredentials']);
    return EMPTY;
  }
}
